'use client';

import { useCallback, useMemo } from 'react';
import { useRouter } from 'next/navigation';

import { appNavigate, appNavigateBack } from '@/utils/navigation/app-navigate';
import { withDyeQuery } from '@/utils/router/with-dye-query';

export interface IAppNavigateOptions {
    /** 透传给 next/navigation router.push/replace */
    scroll?: boolean;
    /**
     * 是否把当前 URL 落地相关五参合并进目标 href，默认 true。
     * 仅在明确需要清参 / 不透传时传 false。
     */
    preserveDye?: boolean;
    /**
     * 跳过活动弹窗释放；默认按 scene 变化自动 release。
     */
    skipActivityRelease?: boolean;
}

export interface IAppRouter {
    push: (href: string, options?: IAppNavigateOptions) => void;
    replace: (href: string, options?: IAppNavigateOptions) => void;
    back: () => void;
    forward: () => void;
    refresh: () => void;
    prefetch: (href: string) => void;
}

/**
 * 站内导航封装：
 * - push / replace 默认透传落地相关五参（染色四参 + landing_timestamp）
 * - 跨 scene 跳转前按需 release 活动弹窗
 * - back 有弹窗时先卸 UI 再退栈
 *
 * 业务侧应使用本 hook，避免直接 useRouter().push/replace/back。
 */
export function useAppRouter(): IAppRouter {
    const router = useRouter();

    const push = useCallback(
        (href: string, options?: IAppNavigateOptions) => {
            appNavigate(router, href, { type: 'push', ...options });
        },
        [router]
    );

    const replace = useCallback(
        (href: string, options?: IAppNavigateOptions) => {
            appNavigate(router, href, { type: 'replace', ...options });
        },
        [router]
    );

    const back = useCallback(() => {
        appNavigateBack(router);
    }, [router]);

    const forward = useCallback(() => {
        router.forward();
    }, [router]);

    const refresh = useCallback(() => {
        router.refresh();
    }, [router]);

    const prefetch = useCallback(
        (href: string) => {
            router.prefetch(withDyeQuery(href));
        },
        [router]
    );

    return useMemo(
        () => ({
            push,
            replace,
            back,
            forward,
            refresh,
            prefetch,
        }),
        [push, replace, back, forward, refresh, prefetch]
    );
}
